import { useState } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { useCreateCourt } from '@/hooks/useCreateCourt'
import { useUpdateCourt } from '@/hooks/useUpdateCourt'
import { ApiError, toApiError } from '@/lib/api/errors'
import {
  COURT_FLOOR_TYPE_CATALOG,
  RESERVATION_UNIT_OPTIONS,
  VENUE_COURT_TYPE_CATALOG,
  type CourtItem,
} from '@/lib/venue/courtTypes'
import {
  emptyCourtFormState,
  toCourtFormState,
  toCourtSaveInput,
  validateCourtForm,
  type CourtFormErrors,
} from '@/lib/venue/courtViewModel'

import { CourtStatusField } from './CourtStatusField'

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

/**
 * 코트 추가/수정 공용 모달(요구사항 2, 설계 §2-1(d)·§2-3).
 *
 * 🚨 이 표면은 서버 `fieldErrors` 봉투가 없다(#597 §5) — 클라이언트 검증을 통과한 뒤 서버가 400 을
 * 주면 검증표상 남는 위반은 "이름 중복"뿐이라 그 메시지를 이름 필드 오류로 바인딩한다(설계 D5).
 */
export function CourtFormModal({
  venueId,
  mode,
  court,
  onClose,
}: {
  venueId: string
  mode: 'create' | 'edit'
  /** `mode === 'edit'` 일 때 필수. */
  court?: CourtItem
  onClose: () => void
}) {
  const [form, setForm] = useState(() => (court ? toCourtFormState(court) : emptyCourtFormState()))
  const [fieldErrors, setFieldErrors] = useState<CourtFormErrors>({})
  const [generalError, setGeneralError] = useState<ApiError | null>(null)

  const createMutation = useCreateCourt(venueId)
  const updateMutation = useUpdateCourt(venueId)
  const isPending = mode === 'create' ? createMutation.isPending : updateMutation.isPending

  const unitLocked = mode === 'edit' && court !== undefined && !court.unitChangeable

  function updateField<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function handleSubmit() {
    const errors = validateCourtForm(form)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    setFieldErrors({})
    setGeneralError(null)

    const input = toCourtSaveInput(form)

    try {
      if (mode === 'create') {
        await createMutation.mutateAsync(input)
      } else if (court) {
        await updateMutation.mutateAsync({ courtId: court.courtId, input })
      }
      onClose()
    } catch (error) {
      const apiError = toApiError(error)
      // 클라이언트 검증을 통과했는데도 400 이면 검증표상 남는 위반은 이름 중복뿐이다(설계 D5).
      if (apiError.status === 400) {
        setFieldErrors({ name: apiError.message })
        return
      }
      setGeneralError(apiError)
    }
  }

  return (
    <Modal title={mode === 'create' ? '코트 추가' : '코트 설정'} onClose={onClose}>
      <div className="space-y-4">
        {generalError ? (
          <Alert tone="danger">
            <p>{generalError.message}</p>
          </Alert>
        ) : null}

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="court-name">
            코트 이름
          </label>
          <Input
            id="court-name"
            value={form.name}
            placeholder="예) 6번 코트"
            aria-invalid={Boolean(fieldErrors.name)}
            onChange={(event) => {
              updateField('name', event.target.value)
            }}
          />
          <FieldError message={fieldErrors.name} />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="court-floor-type">
            바닥 재질
          </label>
          <Select
            id="court-floor-type"
            value={form.floorType}
            onChange={(event) => {
              updateField('floorType', event.target.value as typeof form.floorType)
            }}
          >
            <option value="">미입력</option>
            {COURT_FLOOR_TYPE_CATALOG.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="court-court-type">
              실내/실외
            </label>
            <Select
              id="court-court-type"
              value={form.courtType}
              onChange={(event) => {
                updateField('courtType', event.target.value as typeof form.courtType)
              }}
            >
              <option value="">미입력</option>
              {VENUE_COURT_TYPE_CATALOG.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="court-size-note">
              규격
            </label>
            <Input
              id="court-size-note"
              value={form.sizeNote}
              placeholder="예: 13.4m x 6.1m"
              aria-invalid={Boolean(fieldErrors.sizeNote)}
              onChange={(event) => {
                updateField('sizeNote', event.target.value)
              }}
            />
            <FieldError message={fieldErrors.sizeNote} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="court-weekday-fee">
              평일 요금 (시간당)
            </label>
            <Input
              id="court-weekday-fee"
              inputMode="numeric"
              value={form.weekdayHourlyFee}
              aria-invalid={Boolean(fieldErrors.weekdayHourlyFee)}
              onChange={(event) => {
                updateField('weekdayHourlyFee', event.target.value)
              }}
            />
            <FieldError message={fieldErrors.weekdayHourlyFee} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="court-weekend-fee">
              주말 요금 (시간당)
            </label>
            <Input
              id="court-weekend-fee"
              inputMode="numeric"
              value={form.weekendHourlyFee}
              aria-invalid={Boolean(fieldErrors.weekendHourlyFee)}
              onChange={(event) => {
                updateField('weekendHourlyFee', event.target.value)
              }}
            />
            <FieldError message={fieldErrors.weekendHourlyFee} />
          </div>
        </div>
        {/* 🚨 여기서 넣는 값은 **기본 요금**이다 — 시간대·날짜별 요금은 전용 EP 가 소유해(#852 §2) 이 저장이
            구간을 지우지 않는다. 두 입구의 의미를 사용자에게도 구분해 준다(#868 설계 D10). */}
        <p className="text-xs text-slateish-500">
          시간대·공휴일 등 날짜별 요금은 코트 목록의 &lsquo;요금 설정&rsquo;에서 따로 설정할 수 있어요.
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="court-reservation-unit">
              예약 단위
            </label>
            <Select
              id="court-reservation-unit"
              value={form.reservationUnitMinutes}
              disabled={unitLocked}
              onChange={(event) => {
                updateField('reservationUnitMinutes', Number(event.target.value))
              }}
            >
              {RESERVATION_UNIT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="court-max-reservation">
              최대 예약 시간(분)
            </label>
            <Input
              id="court-max-reservation"
              inputMode="numeric"
              value={form.maxReservationMinutes}
              aria-invalid={Boolean(fieldErrors.maxReservationMinutes)}
              onChange={(event) => {
                updateField('maxReservationMinutes', event.target.value)
              }}
            />
            <FieldError message={fieldErrors.maxReservationMinutes} />
          </div>
        </div>
        {unitLocked && court?.blockNotice ? <p className="text-xs text-slateish-500">{court.blockNotice}</p> : null}

        <div>
          <span className="mb-1.5 block text-sm font-medium text-ink-700">코트 상태</span>
          <CourtStatusField
            value={form.status}
            onChange={(value) => {
              updateField('status', value)
            }}
          />
          <p className="mt-2 text-xs text-slateish-500">
            상태를 바꿔도 이미 확정된 예약은 유지돼요. 겹치는 예약이 있으면 저장 전에 알려드려요.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="button"
            disabled={isPending}
            onClick={() => {
              void handleSubmit()
            }}
          >
            {isPending ? '저장 중…' : mode === 'create' ? '코트 추가하기' : '저장하기'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
